import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { UsersPermissionsUser } from '../types/user.model';
import ApiClientOpen from '@/services/api/axios/apiClientOpen';

// Define the authentication state interface
interface AuthState {
  jwt: string | null;
  user: UsersPermissionsUser | null;
  isLoggedIn: boolean;
  // Actions to update the state
  setCredentials: (jwt: string, user: UsersPermissionsUser) => void;
  updateUser: (user: UsersPermissionsUser) => void;
  clearCredentials: () => void;
  logout: () => Promise<void>;
}

// Function to check if JWT is expired
const isTokenExpired = (token: string): boolean => {
  try {
    // Split the token to get the payload (middle part of JWT)
    const payload = token.split('.')[1];
    // Decode the payload from base64
    const decodedPayload = atob(payload);
    // Parse the JSON payload
    const parsedPayload = JSON.parse(decodedPayload);
    // Get the current time in seconds
    const currentTime = Math.floor(Date.now() / 1000);
    // Check if the token has expired
    return parsedPayload.exp < currentTime;
  } catch (error) {
    // If there's an error parsing the token, assume it's expired
    console.error('Error parsing JWT token:', error);
    return true;
  }
};

// Create the authentication store with persistence
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      jwt: null,
      user: null,
      isLoggedIn: false,
      
      // Set credentials and mark as logged in
      setCredentials: (jwt: string, user: UsersPermissionsUser) => 
        set({ jwt, user, isLoggedIn: true }),
      
      // Clear credentials and mark as logged out
      clearCredentials: () => 
        set({ jwt: null, user: null, isLoggedIn: false }),
      
      // Update user information
      updateUser: (user: UsersPermissionsUser) => 
        set({ isLoggedIn: true, user }),
        
      // Logout function that calls the Strapi logout endpoint
      logout: async () => {
        try {
          // Call the Strapi logout endpoint
          const { user } = useAuthStore.getState();
          await ApiClientOpen.get(`/user-tokens/logout-provider?userId=${user?.id}`);
        } catch (error) {
          console.error('Logout API call failed:', error);
          // Even if the API call fails, we should still clear local credentials
        } finally {
          // Clear credentials regardless of API call result
          set({ jwt: null, user: null, isLoggedIn: false });
        }
      },
    }),
    {
      name: 'auth-storage', // name of the item in localStorage
      storage: createJSONStorage(() => localStorage), // specify localStorage as storage
    }
  )
);

// Helper function to validate current auth state
export const validateAuthState = () => {
  const { jwt } = useAuthStore.getState();
  
  if (jwt && isTokenExpired(jwt)) {
    // Token is expired, clear credentials
    useAuthStore.getState().clearCredentials();
    return false;
  }
  
  return jwt !== null && !isTokenExpired(jwt);
};
